import { useEffect, useRef, useState, type ReactNode } from 'react';
import '../styles/cinematic.css';

/*
 * Small pieces of the cinematic treatment shared by the Landing, Solutions and
 * About pages. Every picture steps aside if its file is missing, so a page never
 * shows a broken image while the photographs are still being uploaded.
 */

function reduced(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Fades the children of every <main-like> section in as they scroll into view. */
export function useReveal(selector = '.mk-section > *, .mk-hero > *') {
  useEffect(() => {
    if (reduced() || typeof IntersectionObserver === 'undefined') return;
    const els = Array.from(document.querySelectorAll<HTMLElement>(selector))
      .filter((e) => !e.classList.contains('cb') && !e.querySelector('.cr'));
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
    }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });
    els.forEach((e) => { e.classList.add('rv'); io.observe(e); });
    return () => { io.disconnect(); els.forEach((e) => e.classList.remove('rv', 'in')); };
  }, [selector]);
}

/** A wide picture carrying one line, in place of a paragraph nobody reads. */
export function CinematicBand({ src, alt, kicker, line, chips }: {
  src: string; alt: string; kicker?: string; line: string; chips?: string[];
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [ok, setOk] = useState(true);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') { setSeen(true); return; }
    const io = new IntersectionObserver(([e]) => { if (e?.isIntersecting) { setSeen(true); io.disconnect(); } }, { threshold: 0.3 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  if (!ok) return null;
  return (
    <div className={`cb${seen ? ' in' : ''}`} ref={ref}>
      <img src={src} alt={alt} loading="lazy" decoding="async" onError={() => setOk(false)} />
      <div className="cb-text">
        {kicker && <small>{kicker}</small>}
        <b>{line}</b>
        {chips && <div className="cb-chips">{chips.map((c) => <span key={c}>{c}</span>)}</div>}
      </div>
    </div>
  );
}

/**
 * A short film of an estate at sunset with the portfolio read out over it, one
 * line at a time. No figure appears: the lines name what is looked after, so
 * nothing on it can be mistaken for the visitor's own numbers. The clip plays
 * on wide screens only and never on a data-saver connection; elsewhere the
 * still frame it was made from stands in.
 */
export function PortfolioFilm({ poster, video, lines, children }: {
  poster: string; video: string; lines: string[]; children?: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [play, setPlay] = useState(false);
  const [on, setOn] = useState(false);
  const [i, setI] = useState(0);
  const [imgOk, setImgOk] = useState(true);
  useEffect(() => {
    const saveData = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData;
    setPlay(!reduced() && !saveData && window.innerWidth >= 760);
  }, []);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') { setOn(true); return; }
    const io = new IntersectionObserver(([e]) => setOn(!!e?.isIntersecting), { threshold: 0.3 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  useEffect(() => {
    if (!on || reduced()) return;
    const t = window.setInterval(() => setI((n) => (n + 1) % lines.length), 2600);
    return () => window.clearInterval(t);
  }, [on, lines.length]);
  if (!imgOk) return null;
  return (
    <div className="pf" ref={ref} role="group" aria-label="A short film of the portfolio">
      <img src={poster} alt="A landscaped estate at sunset" loading="lazy" decoding="async" onError={() => setImgOk(false)} />
      {play && on && <video src={video} poster={poster} autoPlay muted loop playsInline preload="metadata" aria-hidden />}
      <div className="pf-line">
        <b key={i}>{lines[i]}</b>
        <div className="pf-dots" aria-hidden>{lines.map((l, n) => <i key={l} className={n === i ? 'on' : ''} />)}</div>
        {children}
      </div>
    </div>
  );
}
